import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Globals } from '../global/globals';
import { Project } from '../dtos/project';
import { ProjectRole } from '../dtos/project-role';
import { PaginatedResponse } from '../dtos/paginated';
import { ImportJob } from '../project-corpus/domain/types';
import { ImportJobDto, mapImportJobDto } from '../project-corpus/domain/import-job-dto';
import { ProjectProtocol } from '../dtos/project-protocol';

export interface PrismaStats {
  totalImported: number;
  databasesBreakdown: Record<string, number>;
  duplicateRecordsRemoved: number;
  incompleteMetadataRemoved: number;
  firstStageScreened: number;
  firstStageExcluded: number;
  soughtForRetrieval: number;
  reportsNotRetrieved: number;
  assessedForEligibility: number;
  fullTextExcluded: number;
  exclusionReasonsBreakdown: Record<string, number>;
  finalIncluded: number;
}

export interface CompletedProjectPaper {
  id: number;
  paperToProjectId: number;
  title: string;
  authors: string;
  doi: string;
  status: string;
  exclusionReason: string;
  hasExtractedData: boolean;
}

export interface ProjectPaper {
  paperToProjectId: number;
  title: string;
}

export interface PagedResponse<T> {
  items: T[];
  totalItems: number;
}

@Injectable({
  providedIn: 'root',
})
export class ProjectService {
  private baseUri = this.globals.backendUri + '/projects';

  readonly projectUpdated$ = new Subject<void>();
  readonly projectsUpdated$ = new Subject<void>();

  notifyProjectUpdated(): void {
    this.projectUpdated$.next();
  }

  notifyProjectsUpdated(): void {
    this.projectsUpdated$.next();
  }

  constructor(
    private httpClient: HttpClient,
    private globals: Globals,
  ) {}

  getProjectsPaginated(
    page: number,
    size: number,
    q?: string,
    groupId?: string,
    state?: string,
    dateRange?: string,
  ): Observable<PaginatedResponse<Project>> {
    let params = new HttpParams().set('page', page).set('size', size);
    if (q) {
      params = params.set('q', q);
    }
    if (groupId) {
      params = params.set('groupId', groupId);
    }
    if (state) {
      params = params.set('state', state);
    }
    if (dateRange) {
      params = params.set('dateRange', dateRange);
    }
    return this.httpClient.get<any>(this.baseUri, { params }).pipe(
      map((res) => ({
        items: (res.items ?? []).map(
          (project: any) =>
            new Project(
              project.id?.toString() ?? '',
              project.title ?? '',
              project.description ?? '',
              project.groupId?.toString() ?? '',
              project.groupName ?? '',
              project.piUserId?.toString() ?? '',
              project.piName ?? '',
              project.state ?? 'SETUP',
              project.createdAt ?? '',
              project.completedAt ?? '',
              project.userRole ?? 'NONE',
              project.paperCount ?? 0,
              project.exclusionCriteria ?? '',
              project.inclusionCriteria ?? '',
              project.cohenKappaThreshold ?? null,
            ),
        ),
        totalItems: res.totalItems,
        totalPages: res.totalPages,
        currentPage: res.currentPage,
        pageSize: res.pageSize,
      })),
    );
  }

  getProjectsByGroup(groupId: string): Observable<Project[]> {
    return this.httpClient
      .get<any>(this.baseUri, { params: new HttpParams().set('groupId', groupId) })
      .pipe(
        map((res) =>
          (res.items ?? []).map(
            (project: any) =>
              new Project(
                project.id?.toString() ?? '',
                project.title ?? '',
                project.description ?? '',
                project.groupId?.toString() ?? '',
                project.groupName ?? '',
                project.piUserId?.toString() ?? '',
                project.piName ?? '',
                project.state ?? 'SETUP',
                project.createdAt ?? '',
                project.completedAt ?? '',
                project.userRole ?? 'NONE',
                project.paperCount ?? 0,
                project.exclusionCriteria ?? '',
                project.inclusionCriteria ?? '',
                project.cohenKappaThreshold ?? null,
              ),
          ),
        ),
      );
  }

  getProjectById(projectId: string): Observable<Project> {
    return this.httpClient
      .get<any>(`${this.baseUri}/${projectId}`)
      .pipe(
        map(
          (project) =>
            new Project(
              project.id?.toString() ?? '',
              project.title ?? '',
              project.description ?? '',
              project.groupId?.toString() ?? '',
              project.groupName ?? '',
              project.piUserId?.toString() ?? '',
              project.piName ?? '',
              project.state ?? 'SETUP',
              project.createdAt ?? '',
              project.completedAt ?? '',
              project.userRole ?? 'NONE',
              undefined,
              project.exclusionCriteria ?? '',
              project.inclusionCriteria ?? '',
              project.cohenKappaThreshold ?? null,
            ),
        ),
      );
  }

  createProject(groupId: string, projectData: any): Observable<Project> {
    return this.httpClient
      .post<any>(`${this.baseUri}`, { ...projectData, groupId })
      .pipe(
        map(
          (project) =>
            new Project(
              project.id?.toString() ?? '',
              project.title ?? '',
              project.description ?? '',
              project.groupId?.toString() ?? '',
              project.groupName ?? '',
              project.piUserId?.toString() ?? '',
              project.piName ?? '',
              project.state ?? 'SETUP',
              project.createdAt ?? '',
              project.completedAt ?? '',
              project.userRole ?? 'NONE',
              undefined,
              project.exclusionCriteria ?? '',
              project.inclusionCriteria ?? '',
              project.cohenKappaThreshold ?? null,
            ),
        ),
      );
  }

  updateProject(projectId: string, projectData: any): Observable<Project> {
    return this.httpClient
      .put<any>(`${this.baseUri}/${projectId}`, projectData)
      .pipe(
        map(
          (project) =>
            new Project(
              project.id?.toString() ?? '',
              project.title ?? '',
              project.description ?? '',
              project.groupId?.toString() ?? '',
              project.groupName ?? '',
              project.piUserId?.toString() ?? '',
              project.piName ?? '',
              project.state ?? 'SETUP',
              project.createdAt ?? '',
              project.completedAt ?? '',
              project.userRole ?? 'NONE',
              undefined,
              project.exclusionCriteria ?? '',
              project.inclusionCriteria ?? '',
              project.cohenKappaThreshold ?? null,
            ),
        ),
      );
  }

  deleteProject(projectId: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.baseUri}/${projectId}`);
  }

  leaveProject(projectId: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.baseUri}/${projectId}/members/me`);
  }

  getUserProjectRole(projectId: string): Observable<ProjectRole> {
    return this.httpClient
      .get<any>(`${this.baseUri}/${projectId}/role`)
      .pipe(map((data) => new ProjectRole(data.projectId ?? projectId, data.role ?? 'NONE')));
  }

  addResearcherToProject(
    projectId: string,
    userId: string,
    role: string = 'RESEARCHER',
  ): Observable<any> {
    return this.httpClient.post<any>(`${this.baseUri}/${projectId}/members`, {
      userId,
      role,
    });
  }

  getProjectResearchers(
    projectId: string,
    page: number,
    size: number,
    q?: string,
  ): Observable<PaginatedResponse<any>> {
    let params = new HttpParams().set('page', page).set('size', size);
    if (q) {
      params = params.set('q', q);
    }
    return this.httpClient.get<any>(`${this.baseUri}/${projectId}/members`, { params }).pipe(
      map((res) => ({
        items: res.items ?? [],
        totalItems: res.totalItems,
        totalPages: res.totalPages,
        currentPage: res.currentPage,
        pageSize: res.pageSize,
      })),
    );
  }

  getAllProjectResearchers(projectId: string): Observable<any[]> {
    return this.getProjectResearchers(projectId, 0, 100).pipe(map((res) => res.items));
  }

  removeResearcherFromProject(projectId: string, userId: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.baseUri}/${projectId}/members/${userId}`);
  }

  getExclusionReasons(projectId: string): Observable<string[]> {
    return this.httpClient
      .get<{ exclusionReasons: string[] }>(`${this.baseUri}/${projectId}/exclusion-reasons`)
      .pipe(map((data) => data.exclusionReasons));
  }

  setExclusionReasons(
    projectId: string,
    exclusionReasons: string[],
  ): Observable<{ deactivatedReasons: string[] }> {
    return this.httpClient.post<{ deactivatedReasons: string[] }>(
      `${this.baseUri}/${projectId}/exclusion-reasons`,
      {
        exclusionReasons,
      },
    );
  }

  getProjectLocks(
    projectId: string,
    page: number,
    size: number,
    q?: string,
  ): Observable<PaginatedResponse<any>> {
    let params = new HttpParams().set('page', page).set('size', size);
    if (q) {
      params = params.set('q', q);
    }
    return this.httpClient.get<any>(`${this.baseUri}/${projectId}/locks`, { params }).pipe(
      map((res) => ({
        items: res.items ?? [],
        totalItems: res.totalItems,
        totalPages: res.totalPages,
        currentPage: res.currentPage,
        pageSize: res.pageSize,
      })),
    );
  }

  getAllProjectLocks(projectId: string): Observable<any[]> {
    return this.getProjectLocks(projectId, 0, 100).pipe(map((res) => res.items));
  }

  forceReleaseLock(projectId: string, lockId: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.baseUri}/${projectId}/locks/${lockId}`);
  }

  getPrismaStats(projectId: string): Observable<PrismaStats> {
    return this.httpClient.get<PrismaStats>(`${this.baseUri}/${projectId}/prisma-stats`);
  }

  exportBibtex(projectId: string): Observable<string> {
    return this.httpClient.get(`${this.baseUri}/${projectId}/export/bibtex`, {
      responseType: 'text',
    });
  }

  exportExtractionCsv(projectId: string): Observable<Blob> {
    return this.httpClient.get(`${this.baseUri}/${projectId}/export/extraction.csv`, {
      responseType: 'blob',
    });
  }

  getImportJobs(projectId: string): Observable<ImportJob[]> {
    return this.httpClient
      .get<ImportJobDto[]>(`${this.globals.backendUri}/import-jobs`, {
        params: new HttpParams().set('projectId', projectId),
      })
      .pipe(map((dtos) => dtos.map((d) => mapImportJobDto(d, projectId))));
  }

  exportSearchRecordCsv(projectId: string): Observable<Blob> {
    return this.httpClient.get(`${this.baseUri}/${projectId}/export/search-record.csv`, {
      responseType: 'blob',
    });
  }

  getCompletedPapers(
    projectId: string,
    page: number,
    size: number,
    q?: string,
  ): Observable<PagedResponse<CompletedProjectPaper>> {
    const params: Record<string, string | number> = { page, size };
    if (q) params['q'] = q;
    return this.httpClient.get<PagedResponse<CompletedProjectPaper>>(
      `${this.baseUri}/${projectId}/completed/papers`,
      { params },
    );
  }

  getProjectPapers(projectId: string): Observable<ProjectPaper[]> {
    return this.httpClient.get<any>(`${this.baseUri}/${projectId}/papers`).pipe(
      map((res) =>
        (res.items ?? []).map((item: any) => ({
          paperToProjectId: item.ptpId,
          title: item.title ?? '',
        })),
      ),
    );
  }

  getPaperTitle(projectId: string, paperToProjectId: number): Observable<string> {
    return this.httpClient
      .get<any>(`${this.baseUri}/${projectId}/paper-to-project/${paperToProjectId}`)
      .pipe(map((item) => item.title ?? ''));
  }

  searchProjectPapers(projectId: string, query: string): Observable<ProjectPaper[]> {
    const params = { q: query, size: '5' };
    return this.httpClient.get<any>(`${this.baseUri}/${projectId}/papers`, { params }).pipe(
      map((res) =>
        (res.items ?? []).map((item: any) => ({
          paperToProjectId: item.ptpId,
          title: item.title ?? '',
        })),
      ),
    );
  }

  getProjectProtocol(projectId: string): Observable<ProjectProtocol> {
    return this.httpClient.get<ProjectProtocol>(`${this.baseUri}/${projectId}/protocol`);
  }

  saveProjectProtocol(projectId: string, protocol: ProjectProtocol): Observable<ProjectProtocol> {
    return this.httpClient.put<ProjectProtocol>(`${this.baseUri}/${projectId}/protocol`, protocol);
  }
}
